import React, { createContext, useState, useContext, useEffect } from "react";
import accessService from "../../apis/Profile/AccessServiceApi";
import { getAccountInfo } from "../../apis/User";
import { staffService } from "../../apis/Profile/StaffApi";

const bundleHierarchy = {
  freemium: {
    modules: ["Company"],
  },
  board: {
    modules: ["Business"],
    includes: ["freemium"],
  },
  initiator: {
    modules: ["Product", "Feed", "ERP"],
    includes: ["freemium"],
  },
  ambition: {
    modules: ["Ambition"],
    includes: ["initiator", "freemium"],
  },
  together: {
    modules: ["Get Help"],
    includes: ["freemium"],
  },
  others: {
    modules: [
      "Admin",
      "AR",
      "Business",
      "Client",
      "Education",
      "Map",
      "Report",
      "Share",
      "Simulate",
      "Lab",
    ],
  },
};

const normalizeModuleNames = (modules) => {
  if (!Array.isArray(modules)) return [];
  return modules
    .map((m) => {
      if (typeof m === "string") return m;
      if (m && typeof m === "object") return m.name || m.module || m.label;
      return null;
    })
    .filter(Boolean);
};

export const UserModulesContext = createContext({
  userModules: [],
  setUserModules: () => {},
  isModuleAccessible: () => false,
  isLoading: true,
  isStaff: false,
  // ✅ Board member permissions
  isBoardMember: false,
  boardMemberRole: null,
  isAccessManager: false,
  refreshPermissions: () => {},
});

export const UserModulesProvider = ({ children }) => {
  const [userModules, setUserModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isStaff, setIsStaff] = useState(false);

  // ✅ Board member permissions state
  const [isBoardMember, setIsBoardMember] = useState(false);
  const [boardMemberRole, setBoardMemberRole] = useState(null);
  const [isAccessManager, setIsAccessManager] = useState(false);

  const isModuleAccessible = (moduleName) => {
    if (!moduleName) return true;
    // Staff/superuser should never look freemium-locked in the UI
    if (isStaff) return true;
    // If module is directly in user modules, return true
    if (userModules.includes(moduleName)) return true;

    // Check if module is in any bundle hierarchy
    for (const bundleConfig of Object.values(bundleHierarchy)) {
      // Check if module is in the current bundle's modules
      if (bundleConfig.modules.includes(moduleName)) {
        // If module is in this bundle, but not in user modules, it should be locked
        return false;
      }
    }

    // If module is not in any bundle hierarchy, it's considered accessible
    return true;
  };

  useEffect(() => {
    const fetchUserModules = async () => {
      try {
        setIsLoading(true);
        const [modules, staffStatus] = await Promise.all([
          accessService.getUserModules().catch((error) => {
            console.error("Failed to fetch user modules:", error);
            return [];
          }),
          staffService.getUserStaffStatus().catch((error) => {
            console.error("Failed to fetch staff status:", error);
            return false;
          }),
        ]);
        setUserModules(normalizeModuleNames(modules));
        setIsStaff(Boolean(staffStatus));

        // ✅ Also fetch board member permissions
        try {
          const response = await getAccountInfo();
          const data = response?.data?.data || response?.data || {};
          setIsBoardMember(data.is_board_member || false);
          setBoardMemberRole(data.board_member_role || null);
          setIsAccessManager(data.is_access_manager || false);
          // Some environments expose staff/superuser on /me as well
          if (data.is_staff || data.is_superuser) {
            setIsStaff(true);
          }
        } catch (permError) {
          console.error("Failed to fetch board member permissions:", permError);
          setIsBoardMember(false);
          setBoardMemberRole(null);
          setIsAccessManager(false);
        }
      } finally {
        setIsLoading(false);
      }
    };

    const checkTokenAndFetchModules = () => {
      const accessToken = localStorage.getItem("access_token");
      if (accessToken) {
        fetchUserModules();
      } else {
        const timer = setTimeout(() => {
          const laterToken = localStorage.getItem("access_token");
          if (laterToken) {
            fetchUserModules();
          } else {
            setIsLoading(false);
          }
        }, 500);

        return () => clearTimeout(timer);
      }
    };

    checkTokenAndFetchModules();
  }, []);

  // ✅ Refresh function for manual permission reload (e.g., after approval/deactivation)
  const refreshPermissions = async () => {
    try {
      const [response, staffStatus] = await Promise.all([
        getAccountInfo(),
        staffService.getUserStaffStatus().catch(() => false),
      ]);
      const data = response?.data?.data || response?.data || {};
      setIsBoardMember(data.is_board_member || false);
      setBoardMemberRole(data.board_member_role || null);
      setIsAccessManager(data.is_access_manager || false);
      setIsStaff(Boolean(staffStatus || data.is_staff || data.is_superuser));
    } catch (error) {
      console.error("Failed to refresh permissions:", error);
    }
  };

  return (
    <UserModulesContext.Provider
      value={{
        userModules,
        setUserModules,
        isModuleAccessible,
        isLoading,
        isStaff,
        // ✅ Board member permissions
        isBoardMember,
        boardMemberRole,
        isAccessManager,
        refreshPermissions,
      }}
    >
      {children}
    </UserModulesContext.Provider>
  );
};

export const useUserModules = () => {
  const context = useContext(UserModulesContext);
  if (!context) {
    throw new Error("useUserModules must be used within a UserModulesProvider");
  }
  return context;
};
